import type { DayPlan, Stop } from "../lib/types";

/**
 * Når man kommer fram, og om det er lyst da.
 *
 * Appen visste allerede når sola går opp og ned — serveren regner det per
 * stopp og per dag, og tallene står i `Stop.golden` og `DayPlan.daylight`. Den
 * visste bare ikke *når du er der*. Dagslyset ble derfor vist som en
 * egenskap ved dagen, og et stopp som ligger sist på en åtte timers etappe i
 * oktober så like lyst ut som det første.
 *
 * Modulen legger ankomsttiden på: den summerer etappetidene og oppholdene
 * framover gjennom dagen, og sammenholder resultatet med soloppgang og
 * solnedgang serveren allerede har regnet. Ingen ny solmatematikk — to
 * uavhengige utregninger av samme solnedgang ville før eller siden vist to
 * forskjellige klokkeslett, og da er begge mistenkelige.
 */

/** Når en reisedag begynner. Frokost, pakking og utsjekk er tatt med. */
const DAY_STARTS_AT_HOUR = 9;

/** Under dette regnes det som skumring framfor mørke. */
const DUSK_MARGIN_MINUTES = 30;

export interface StopSunlight {
  /** Anslått ankomst, ISO. Bygget på etappetidene og oppholdene før stoppet. */
  arrival: string;
  sunrise: string | null;
  sunset: string | null;
  /** Om sola er oppe når man kommer fram. */
  isDaylight: boolean;
  /** Beste fotolys på stedet den dagen, fra serverens golden hour. */
  goldenHour: string | null;
  /**
   * Sant når man kommer fram etter solnedgang.
   *
   * Skilt fra `!isDaylight` fordi de ikke er det samme: å komme til
   * Trolltunga før soloppgang er en plan, å komme fram etter solnedgang er en
   * feil. Midnattssol og mørketid håndteres av at `sunset` mangler.
   */
  darknessWarning: boolean;
}

/**
 * Klokkeslettet `hour` i norsk tid på en gitt dato, som UTC-millisekunder.
 *
 * Sto som en fast `+02:00`, og det er riktig fra slutten av mars til slutten
 * av oktober og feil resten av året. Én time høres lite ut, men vinteren er
 * nettopp når det betyr noe: en oktoberdag i Nord-Norge har solnedgang rundt
 * fire, og en ankomst regnet en time for tidlig er forskjellen på en advarsel
 * som kommer og en som ikke gjør det.
 *
 * Løses ved å prøve begge norske forskyvningene og beholde den som faktisk gir
 * riktig klokkeslett i Oslo. Sommertiden snur klokka to om natta, så et
 * tidspunkt midt på formiddagen er alltid entydig — det finnes ingen dato der
 * begge svarene stemmer.
 */
function osloTime(date: string, hour: number): number {
  const hh = String(hour).padStart(2, "0");
  const clockIn = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Oslo",
    hour: "2-digit",
    hour12: false,
  });

  for (const offset of ["+01:00", "+02:00"]) {
    const at = Date.parse(`${date}T${hh}:00:00${offset}`);
    if (Number.isNaN(at)) continue;
    if (Number(clockIn.format(at)) === hour) return at;
  }

  return NaN;
}

/**
 * Ankomsttid for hvert stopp i en dag, som id → ISO-tid.
 *
 * Klokka starter kl. 09 og går framover: reisetiden på etappen inn til
 * stoppet, så oppholdet der, så neste etappe. Etappen inn til dagens første
 * stopp teller med — det er kjøringen fra i går kveld, og den er grunnen til
 * at dag to sjelden begynner med et stopp klokka ni.
 *
 * Tidene er anslag, ikke en rutetabell. De er likevel langt nærmere sannheten
 * enn å behandle alle stoppene i en dag som om de skjer samtidig.
 */
export function arrivalTimes(day: DayPlan): Map<string, string> {
  const times = new Map<string, string>();
  if (!day.date) return times;

  let clock = osloTime(day.date, DAY_STARTS_AT_HOUR);
  if (Number.isNaN(clock)) return times;

  for (const stop of day.stops) {
    const leg = day.connections.find((connection) => connection.toStopId === stop.id);
    clock += (leg?.hours ?? 0) * 3_600_000;
    times.set(stop.id, new Date(clock).toISOString());
    clock += stop.dwellHours * 3_600_000;
  }

  return times;
}

/**
 * Sol og dagslys for ett stopp, på ankomsttidspunktet.
 *
 * Returnerer undefined uten dato: uten en dag å regne for er soloppgang et
 * spørsmål uten svar, og en tom «Sol og dagslys»-boks er verre enn ingen.
 */
export function stopSunlight(stop: Stop, day: DayPlan): StopSunlight | undefined {
  const arrival = arrivalTimes(day).get(stop.id);
  if (!arrival) return undefined;

  const sunrise = day.daylight?.sunrise ?? null;
  const sunset = day.daylight?.sunset ?? null;
  const at = Date.parse(arrival);

  /*
   * Midnattssol og mørketid er ikke «mangler data».
   *
   * Nord for polarsirkelen har juni ingen solnedgang og desember ingen
   * soloppgang, og serveren sier det ved å la feltene stå tomme og sette
   * flaggene. Uten dette ville en juninatt i Tromsø blitt regnet som mørk
   * fordi `sunset` var null.
   */
  if (day.daylight?.midnightSun) {
    return { arrival, sunrise, sunset, isDaylight: true, goldenHour: bestGolden(stop), darknessWarning: false };
  }
  if (day.daylight?.polarNight) {
    return { arrival, sunrise, sunset, isDaylight: false, goldenHour: bestGolden(stop), darknessWarning: true };
  }

  const up = sunrise ? Date.parse(sunrise) : null;
  const down = sunset ? Date.parse(sunset) : null;
  const isDaylight = up !== null && down !== null ? at >= up && at <= down : true;

  return {
    arrival,
    sunrise,
    sunset,
    isDaylight,
    goldenHour: bestGolden(stop),
    // Skumringen teller ikke som mørke: en halvtime etter solnedgang ser man
    // fortsatt fjorden, og en advarsel der ville vært en advarsel for mye.
    darknessWarning: down !== null && at > down + DUSK_MARGIN_MINUTES * 60_000,
  };
}

/** Beste fotolys på stedet, slik serveren regnet det. */
function bestGolden(stop: Stop): string | null {
  return stop.golden?.best ?? stop.golden?.evening?.from ?? stop.golden?.morning?.from ?? null;
}
